import { Controller } from "@hotwired/stimulus"

// "By the numbers": the figures count up fast each time the section scrolls into
// view, over a full-bleed dot field. Individual green "files" enter at random
// times on random rows and travel left to right at their own speeds, each
// leaving a fading trail behind it, as if documents are moving through the
// system. Animation runs only while in view; figures stay static and the field
// is drawn once under reduced motion.
const COMPACT = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 0 })
const DURATION = 900 // ms count-up
const PITCH = 12 // px between dots
const DOT = 6 // px dot size
const HEAD = 8 // px sharp leading edge of a file
const TRAIL = 44 // px fading trail behind a file
const EMPTY = "rgb(209, 213, 219)" // gray-300 resting dot
const GREEN = [22, 163, 74] // green-600

export default class extends Controller {
  static targets = ["number", "wave"]

  connect() {
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    if (this.hasWaveTarget) {
      this.sizeCanvas()
      this.onResize = () => this.sizeCanvas()
      window.addEventListener("resize", this.onResize)
    }

    if (this.reduced) {
      this.numberTargets.forEach((el) => (el.textContent = el.dataset.display))
      this.drawField()
      return
    }

    this.numberTargets.forEach((el) => (el.textContent = "0"))
    this.countGen = 0
    this.observer = new IntersectionObserver(([entry]) => (entry.isIntersecting ? this.enter() : this.leave()), {
      threshold: 0.2
    })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.leave()
    if (this.observer) this.observer.disconnect()
    if (this.onResize) window.removeEventListener("resize", this.onResize)
  }

  enter() {
    // Restart the count-up on every entry; the generation token cancels any
    // count loop still running from a previous, quick scroll-through.
    this.countGen += 1
    const gen = this.countGen
    this.countStart = null
    this.numberTargets.forEach((el) => (el.textContent = "0"))
    requestAnimationFrame((t) => this.count(t, gen))

    if (!this.waving && this.hasWaveTarget) {
      this.waving = true
      this.lastTimestamp = null
      this.waveFrame = requestAnimationFrame((t) => this.tickField(t))
    }
  }

  leave() {
    this.waving = false
    if (this.waveFrame) cancelAnimationFrame(this.waveFrame)
  }

  count(timestamp, gen) {
    if (gen !== this.countGen) return // superseded by a newer entry
    if (this.countStart == null) this.countStart = timestamp
    const progress = Math.min((timestamp - this.countStart) / DURATION, 1)
    const eased = 1 - Math.pow(1 - progress, 3)
    this.numberTargets.forEach((el) => {
      el.textContent =
        progress >= 1 ? el.dataset.display : `${COMPACT.format(Math.round(Number(el.dataset.total) * eased))}+`
    })
    if (progress < 1) requestAnimationFrame((t) => this.count(t, gen))
  }

  tickField(timestamp) {
    if (!this.waving) return
    const dt = this.lastTimestamp == null ? 16 : Math.min(timestamp - this.lastTimestamp, 50)
    this.lastTimestamp = timestamp

    this.files.forEach((file) => {
      file.x += file.speed * dt
      if (file.x > this.width + HEAD * 2) Object.assign(file, this.spawnFile(false))
    })

    this.drawField()
    this.waveFrame = requestAnimationFrame((t) => this.tickField(t))
  }

  spawnFile(initial) {
    return {
      row: Math.floor(Math.random() * this.rows),
      x: initial ? Math.random() * this.width : -HEAD - Math.random() * 220,
      speed: 0.05 + Math.random() * 0.16, // px/ms, varied
      bright: 0.75 + Math.random() * 0.25
    }
  }

  sizeCanvas() {
    const canvas = this.waveTarget
    const dpr = window.devicePixelRatio || 1
    this.width = canvas.clientWidth
    this.height = canvas.clientHeight
    canvas.width = this.width * dpr
    canvas.height = this.height * dpr
    this.ctx = canvas.getContext("2d")
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    this.rows = Math.max(1, Math.floor(this.height / PITCH))
    const count = Math.min(Math.max(Math.round(this.width / 90), 8), 30)
    this.files = Array.from({ length: count }, () => this.spawnFile(true))
  }

  drawField() {
    const ctx = this.ctx
    if (!ctx) return
    const offset = PITCH / 2

    ctx.clearRect(0, 0, this.width, this.height)
    ctx.fillStyle = EMPTY
    for (let y = offset; y < this.height; y += PITCH) {
      for (let x = offset; x < this.width; x += PITCH) ctx.fillRect(x, y, DOT, DOT)
    }

    this.files.forEach((file) => {
      const gy = offset + file.row * PITCH
      const min = file.x - TRAIL * 3.5
      const max = file.x + HEAD * 3
      for (let x = offset; x < this.width; x += PITCH) {
        if (x < min || x > max) continue
        // Sharp leading edge ahead of the head, exponential fade in the trail behind.
        let intensity
        if (x >= file.x) {
          const d = (x - file.x) / HEAD
          intensity = Math.exp(-d * d)
        } else {
          intensity = Math.exp((x - file.x) / TRAIL)
        }
        intensity *= file.bright
        if (intensity < 0.05) continue
        ctx.fillStyle = `rgb(${Math.round(209 + (GREEN[0] - 209) * intensity)},${Math.round(
          213 + (GREEN[1] - 213) * intensity
        )},${Math.round(219 + (GREEN[2] - 219) * intensity)})`
        ctx.fillRect(x, gy, DOT, DOT)
      }
    })
  }
}
